import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, AlertTriangle, TrendingUp, Users, Zap, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface UsageStats {
  totalRequestsToday: number;
  estimatedCostToday: number;
  requestsByUser: { user_id: string; count: number; email?: string }[];
  requestsByEndpoint: { endpoint: string; count: number }[];
  last7Days: { date: string; count: number; cost: number }[];
  spikeWarnings: string[];
}

export default function AdminUsageDashboard() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminAndLoadStats();
  }, []);

  const checkAdminAndLoadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/");
        return;
      }

      // Check if user is admin
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .single();

      if (!roleData) {
        setIsAdmin(false);
        return;
      }

      setIsAdmin(true);
      await loadStats();
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
    }
  };

  const loadStats = async () => {
    try {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

      // Get all logs from last 7 days
      const { data: logs, error } = await supabase
        .from("api_usage_logs")
        .select("*")
        .gte("created_at", sevenDaysAgo)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const typedLogs = logs as {
        id: string;
        user_id: string;
        endpoint: string;
        model: string | null;
        tokens_input: number;
        tokens_output: number;
        estimated_cost: number;
        created_at: string;
      }[];

      // Calculate today's stats
      const todayLogs = typedLogs.filter(log => log.created_at >= todayStart);
      const totalRequestsToday = todayLogs.length;
      const estimatedCostToday = todayLogs.reduce((sum, log) => sum + (log.estimated_cost || 0), 0);

      // Requests by user
      const userCounts: Record<string, number> = {};
      typedLogs.forEach(log => {
        userCounts[log.user_id] = (userCounts[log.user_id] || 0) + 1;
      });
      const requestsByUser = Object.entries(userCounts)
        .map(([user_id, count]) => ({ user_id, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Requests by endpoint
      const endpointCounts: Record<string, number> = {};
      typedLogs.forEach(log => {
        endpointCounts[log.endpoint] = (endpointCounts[log.endpoint] || 0) + 1;
      });
      const requestsByEndpoint = Object.entries(endpointCounts)
        .map(([endpoint, count]) => ({ endpoint, count }))
        .sort((a, b) => b.count - a.count);

      // Last 7 days chart data
      const dailyStats: Record<string, { count: number; cost: number }> = {};
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        dailyStats[dateStr] = { count: 0, cost: 0 };
      }
      typedLogs.forEach(log => {
        const dateStr = log.created_at.split('T')[0];
        if (dailyStats[dateStr]) {
          dailyStats[dateStr].count++;
          dailyStats[dateStr].cost += log.estimated_cost || 0;
        }
      });
      const last7Days = Object.entries(dailyStats).map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        count: data.count,
        cost: data.cost
      }));

      // Spike detection (if today > 2x average of last 6 days)
      const spikeWarnings: string[] = [];
      const avgPrevious = last7Days.slice(0, -1).reduce((sum, d) => sum + d.count, 0) / 6;
      if (totalRequestsToday > avgPrevious * 2 && avgPrevious > 0) {
        spikeWarnings.push(`Today's requests (${totalRequestsToday}) are ${(totalRequestsToday / avgPrevious).toFixed(1)}x higher than the 6-day average (${avgPrevious.toFixed(0)})`);
      }
      if (estimatedCostToday > 5) {
        spikeWarnings.push(`Today's estimated cost ($${estimatedCostToday.toFixed(2)}) exceeds $5 threshold`);
      }

      setStats({
        totalRequestsToday,
        estimatedCostToday,
        requestsByUser,
        requestsByEndpoint,
        last7Days,
        spikeWarnings
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">You don't have permission to view this page.</p>
            <Button onClick={() => navigate("/")}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading || isAdmin === null) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">API Usage Dashboard</h1>
            <p className="text-muted-foreground">Admin-only usage monitoring</p>
          </div>
          <Badge variant="secondary" className="ml-auto">Admin Only</Badge>
        </div>

        {/* Spike Warnings */}
        {stats?.spikeWarnings && stats.spikeWarnings.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Usage Spike Detected</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside mt-2">
                {stats.spikeWarnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Today's Requests</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalRequestsToday || 0}</div>
              <p className="text-xs text-muted-foreground">API calls today</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Est. Cost Today</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats?.estimatedCostToday.toFixed(4) || '0.00'}</div>
              <p className="text-xs text-muted-foreground">OpenAI API cost</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.requestsByUser.length || 0}</div>
              <p className="text-xs text-muted-foreground">Users this week</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Top Endpoint</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold truncate">
                {stats?.requestsByEndpoint[0]?.endpoint || 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats?.requestsByEndpoint[0]?.count || 0} requests
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Last 7 Days Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.last7Days || []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="date" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value: number, name: string) => [
                      name === 'cost' ? `$${value.toFixed(4)}` : value,
                      name === 'cost' ? 'Est. Cost' : 'Requests'
                    ]}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* By User */}
          <Card>
            <CardHeader>
              <CardTitle>Requests by User (Top 10)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.requestsByUser.length === 0 && (
                  <p className="text-muted-foreground text-sm">No data yet</p>
                )}
                {stats?.requestsByUser.map((user, i) => (
                  <div key={user.user_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">{i + 1}.</span>
                      <span className="font-mono text-sm truncate max-w-[200px]">
                        {user.user_id.slice(0, 8)}...
                      </span>
                    </div>
                    <Badge variant="secondary">{user.count} requests</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* By Endpoint */}
          <Card>
            <CardHeader>
              <CardTitle>Requests by Endpoint</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.requestsByEndpoint.length === 0 && (
                  <p className="text-muted-foreground text-sm">No data yet</p>
                )}
                {stats?.requestsByEndpoint.map((ep, i) => (
                  <div key={ep.endpoint} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">{i + 1}.</span>
                      <span className="font-medium text-sm">{ep.endpoint}</span>
                    </div>
                    <Badge variant="outline">{ep.count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}